<script setup>
import Shepherd from "shepherd.js"
import { withQuery } from "ufo"
import { useConfigStore } from "@core/stores/config"

defineOptions({
  inheritAttrs: false,
})

const configStore = useConfigStore()
const isAppSearchBarVisible = ref(false)
const isLoading = ref(false)
const router = useRouter()
const { t, te } = useI18n()

// Empty-state shortcuts (shown before the user types anything). Routes here
// must exist; the demo Vuexy dashboards-* etc. routes were removed.
const suggestionGroups = computed(() => [
  {
    title: t("app_search.shortcuts"),
    content: [
      {
        icon: "tabler-package",
        title: t("app_search.shortcut_incoming_parcels"),
        url: { name: "shipment-incoming-parcels" },
      },
      {
        icon: "tabler-users",
        title: t("app_search.shortcut_customers"),
        url: { name: "shipment-customers" },
      },
      {
        icon: "tabler-ship",
        title: t("app_search.shortcut_containers"),
        url: { name: "shipment-container-arrivals" },
      },
      {
        icon: "tabler-file-invoice",
        title: t("app_search.shortcut_invoices"),
        url: { name: "shipment-warehouse-invoices" },
      },
      {
        icon: "tabler-calculator",
        title: t("app_search.shortcut_cotation"),
        url: { name: "shipment-warehouse-cotation-index" },
      },
      {
        icon: "tabler-file-spreadsheet",
        title: t("app_search.shortcut_pl_files"),
        url: { name: "shipment-warehouse-pl-files-index" },
      },
    ],
  },
])

const searchQuery = ref("")
const searchResult = ref([])

// Status → Vuetify color, mirrored from statusUtils.js for visual parity
// with the rest of the app. Keys are canonical API status strings.
/* eslint-disable camelcase */
const STATUS_COLORS = {
  pending: "warning",
  received: "info",
  packaged: "primary",
  shipped: "secondary",
  arrived: "info",
  counter_verified: "success",
  ready_for_pickup: "success",
  delivered: "success",
  paid: "success",
  quoted: "info",
  cancelled: "error",
  draft: "secondary",
}
/* eslint-enable camelcase */

// Shape backend response into the { title, children: [...] } format that
// AppBarSearch.vue expects. Each group becomes one section in the modal.
const GROUP_META = {
  customers: { i18nKey: "app_search.group_customers",  icon: "tabler-user" },
  parcels: { i18nKey: "app_search.group_parcels",    icon: "tabler-package" },
  containers: { i18nKey: "app_search.group_containers", icon: "tabler-ship" },
  invoices: { i18nKey: "app_search.group_invoices",   icon: "tabler-file-invoice" },
}

const formatGroup = (key, group) => {
  if (!group || group.total === 0) return null
  const meta = GROUP_META[key]

  return {
    key,
    title: `${t(meta.i18nKey)} (${group.total})`,
    children: group.items.map(item => buildEntry(key, item, meta.icon)),
  }
}

const buildEntry = (key, item, icon) => {
  switch (key) {
  case "customers":
    return {
      icon: "tabler-user",
      title: item.name || item.client_id || `#${item.id}`,
      subtitle: [item.client_id, item.phone].filter(Boolean).join(" · "),
      url: item.route,
    }
  case "parcels":
    return {
      kind: "parcel",
      icon: "tabler-package",
      title: item.barcode,
      subtitle: [item.customer_name, item.bag_code].filter(Boolean).join(" · "),
      statusColor: STATUS_COLORS[item.status] || "secondary",
      statusLabel: te(`shipment.statuses.${item.status}`)
        ? t(`shipment.statuses.${item.status}`)
        : item.status,
      url: item.route,

      // Enriched detail (rendered by the parcel-specific result template)
      imageUrl: item.image_url,
      imageCount: item.image_count,
      transport: item.transport, // 0=air, 1=sea
      weight: item.weight,
      teamCode: item.team_code,
      teamColor: item.team_color,
      customerName: item.customer_name,
      customerClientId: item.customer_client_id,
      customerPhone: item.customer_phone,
      customerRoute: item.customer_route,
      containerReference: item.container_reference,
      containerArrivalDate: item.container_arrival_date,
      containerRoute: item.container_route,
      clientGroupReference: item.client_group_reference,
      groupRoute: item.group_route,
    }
  case "containers":
    return {
      icon: "tabler-ship",
      title: item.reference_code || `#${item.id}`,
      subtitle: [
        item.container_number,
        item.arrival_ref,
        item.transport_type,
      ].filter(Boolean).join(" · "),
      url: item.route,
    }
  case "invoices":
    return {
      icon: "tabler-file-invoice",
      title: item.quote_number,
      subtitle: [item.customer_name, item.external_invoice_number].filter(Boolean).join(" · "),
      statusColor: STATUS_COLORS[item.status] || "secondary",
      statusLabel: te(`shipment.invoice_statuses.${item.status}`)
        ? t(`shipment.invoice_statuses.${item.status}`)
        : item.status,
      url: item.route,
    }
  default:
    return { icon, title: item.title || `#${item.id}`, url: item.route }
  }
}

// For each container found, direct links to its Fichiers PL and Cotation
// pages (the container result itself opens the container list).
const containerPagesGroup = group => {
  const items = (group?.items || []).filter(item => item.id)
  if (!items.length) return null

  return {
    key: "container_pages",
    title: t("app_search.group_container_pages"),
    children: items.flatMap(item => [
      {
        icon: "tabler-file-spreadsheet",
        title: `${t("app_search.shortcut_pl_files")} — ${item.reference_code || `#${item.id}`}`,
        url: `/shipment/warehouse/pl-files/${item.id}`,
      },
      {
        icon: "tabler-calculator",
        title: `${t("app_search.shortcut_cotation")} — ${item.reference_code || `#${item.id}`}`,
        url: `/shipment/warehouse/cotation/${item.id}`,
      },
    ]),
  }
}

let inflight = null

const fetchResults = async () => {
  const q = searchQuery.value.trim()
  if (q.length < 2) {
    searchResult.value = []
    isLoading.value = false
    
    return
  }
  isLoading.value = true

  // Cancel-in-flight pattern: ignore stale responses if the user kept typing.
  const myToken = Symbol("req")

  inflight = myToken

  try {
    const { data } = await useApi(
      withQuery("/shipment/app-bar/search", { q }),
    )

    if (inflight !== myToken) return
    const groups = data.value?.groups || {}

    searchResult.value = [
      ...["customers", "parcels", "containers", "invoices"].map(k => formatGroup(k, groups[k])),
      containerPagesGroup(groups.containers),
    ].filter(Boolean)
  } catch (e) {
    if (inflight === myToken) searchResult.value = []
    console.error("Global search failed:", e)
  } finally {
    if (inflight === myToken) isLoading.value = false
  }
}

// Debounce to avoid hammering Meilisearch on every keystroke. 180ms feels
// instant on a fast index.
let debounceTimer = null
watch(searchQuery, () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(fetchResults, 180)
})

const closeSearchBar = () => {
  isAppSearchBarVisible.value = false
  searchQuery.value = ""
}

const redirectToSuggestedPage = selected => {
  if (!selected?.url) return
  router.push(selected.url)
  closeSearchBar()
}

const LazyAppBarSearch = defineAsyncComponent(() =>
  import("@core/components/AppBarSearch.vue"),
)
</script>

<template>
  <div
    class="d-flex align-center cursor-pointer"
    v-bind="$attrs"
    style="user-select: none"
    @click="isAppSearchBarVisible = !isAppSearchBarVisible"
  >
    <IconBtn @click="Shepherd.activeTour?.cancel()">
      <VIcon icon="tabler-search" />
    </IconBtn>

    <span
      v-if="configStore.appContentLayoutNav === 'vertical'"
      class="d-none d-md-flex align-center text-disabled ms-2"
      @click="Shepherd.activeTour?.cancel()"
    >
      <span class="me-2">{{ t("app_search.placeholder") }}</span>
      <span class="meta-key">&#8984;K</span>
    </span>
  </div>

  <LazyAppBarSearch
    v-model:is-dialog-visible="isAppSearchBarVisible"
    :search-results="searchResult"
    :is-loading="isLoading"
    @search="searchQuery = $event"
  >
    <template #suggestions>
      <VCardText class="app-bar-search-suggestions pa-12">
        <VRow v-if="suggestionGroups">
          <VCol
            v-for="suggestion in suggestionGroups"
            :key="suggestion.title"
            cols="12"
            sm="6"
          >
            <p
              class="custom-letter-spacing text-disabled text-uppercase py-2 px-4 mb-0"
              style="font-size: 0.75rem; line-height: 0.875rem"
            >
              {{ suggestion.title }}
            </p>
            <VList class="card-list">
              <VListItem
                v-for="item in suggestion.content"
                :key="item.title"
                class="app-bar-search-suggestion mx-4 mt-2"
                @click="redirectToSuggestedPage(item)"
              >
                <VListItemTitle>{{ item.title }}</VListItemTitle>
                <template #prepend>
                  <VIcon
                    :icon="item.icon"
                    size="20"
                    class="me-n1"
                  />
                </template>
              </VListItem>
            </VList>
          </VCol>
        </VRow>
      </VCardText>
    </template>

    <template #noDataSuggestion>
      <div class="mt-9">
        <span class="d-flex justify-center text-disabled mb-2">
          {{ t("app_search.empty_hint") }}
        </span>
      </div>
    </template>

    <template #searchResult="{ item }">
      <VListSubheader class="text-disabled custom-letter-spacing font-weight-regular ps-4">
        {{ item.title }}
      </VListSubheader>

      <!--
        Parcel rows get a richer layout: image thumbnail, big barcode,
        customer name, and clickable chips for container/group/customer. 
      -->
      <template
        v-for="list in item.children"
        :key="list.title + (list.subtitle || '')"
      >
        <VListItem
          v-if="list.kind === 'parcel'"
          class="parcel-result-row"
          :to="list.url"
          @click="closeSearchBar"
        >
          <template #prepend>
            <VAvatar
              v-if="list.imageUrl"
              :image="list.imageUrl"
              size="44"
              rounded="lg"
              class="me-2"
            />
            <VAvatar
              v-else
              color="primary"
              variant="tonal"
              size="44"
              rounded="lg"
              class="me-2"
            >
              <VIcon
                icon="tabler-package"
                size="22"
              />
            </VAvatar>
          </template>

          <div class="d-flex flex-column">
            <div class="d-flex align-center gap-2 flex-wrap">
              <span class="font-weight-bold text-body-1">{{ list.title }}</span>
              <VChip
                v-if="list.statusLabel"
                size="x-small"
                variant="tonal"
                :color="list.statusColor"
              >
                {{ list.statusLabel }}
              </VChip>
              <VChip
                v-if="list.teamCode"
                size="x-small"
                variant="tonal"
                color="primary"
              >
                {{ list.teamCode }}
              </VChip>
              <VChip
                v-if="list.transport === 0"
                size="x-small"
                variant="tonal"
                color="info"
                prepend-icon="tabler-plane"
              >
                {{ t("shipment.air") }}
              </VChip>
              <VChip
                v-else-if="list.transport === 1"
                size="x-small"
                variant="tonal"
                color="info"
                prepend-icon="tabler-ship"
              >
                {{ t("shipment.sea") }}
              </VChip>
              <span
                v-if="list.weight"
                class="text-caption text-disabled"
              >{{ list.weight }} kg</span>
            </div>
            <div class="d-flex align-center gap-2 flex-wrap mt-1">
              <span
                v-if="list.customerName"
                class="text-body-2"
              >
                <VIcon
                  icon="tabler-user"
                  size="14"
                  class="me-1"
                />
                {{ list.customerName }}
                <span
                  v-if="list.customerClientId"
                  class="text-caption text-disabled ms-1"
                >{{ list.customerClientId }}</span>
              </span>
              <VChip
                v-if="list.containerReference"
                size="x-small"
                variant="outlined"
                color="info"
                prepend-icon="tabler-ship"
                :to="list.containerRoute"
                @click.stop="closeSearchBar"
              >
                {{ list.containerReference }}
              </VChip>
              <VChip
                v-if="list.clientGroupReference && list.clientGroupReference !== list.containerReference"
                size="x-small"
                variant="outlined"
                color="secondary"
                prepend-icon="tabler-users"
                :to="list.groupRoute"
                @click.stop="closeSearchBar"
              >
                {{ list.clientGroupReference }}
              </VChip>
              <span
                v-if="list.imageCount > 1"
                class="text-caption text-disabled"
              >
                +{{ list.imageCount - 1 }} {{ t("shipment.images") }}
              </span>
            </div>
          </div>
          <template #append>
            <VIcon
              size="20"
              icon="tabler-corner-down-left"
              class="enter-icon flip-in-rtl"
            />
          </template>
        </VListItem>

        <!--
          Other entity types (customer, container, invoice) keep the
          compact one-line layout. 
        -->
        <VListItem
          v-else
          :to="list.url"
          @click="closeSearchBar"
        >
          <template #prepend>
            <VIcon
              size="20"
              :icon="list.icon"
              class="me-n1"
            />
          </template>
          <template #append>
            <VChip
              v-if="list.statusLabel"
              size="x-small"
              variant="tonal"
              :color="list.statusColor"
              class="me-2"
            >
              {{ list.statusLabel }}
            </VChip>
            <VIcon
              size="20"
              icon="tabler-corner-down-left"
              class="enter-icon flip-in-rtl"
            />
          </template>
          <VListItemTitle class="font-weight-medium">
            {{ list.title }}
          </VListItemTitle>
          <VListItemSubtitle
            v-if="list.subtitle"
            class="text-caption text-disabled"
          >
            {{ list.subtitle }}
          </VListItemSubtitle>
        </VListItem>
      </template>
    </template>
  </LazyAppBarSearch>
</template>

<style lang="scss">
@use "@styles/variables/vuetify.scss";

.meta-key {
  border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
  block-size: 1.5625rem;
  font-size: 0.8125rem;
  line-height: 1.3125rem;
  padding-block: 0.125rem;
  padding-inline: 0.25rem;
}

.app-bar-search-dialog {
  .custom-letter-spacing {
    letter-spacing: 0.8px;
  }

  .card-list {
    --v-card-list-gap: 8px;
  }
}
</style>
